import { computed, onUnmounted, ref } from 'vue';

import { CameraType } from '@/types/components';

const videoElement = ref<HTMLVideoElement | null>(null);
const stream = ref<MediaStream | null>(null);

const isCameraOn = computed(() => {
  return (
    stream.value !== null &&
    stream.value.getTracks().some((track) => track.readyState === 'live')
  );
});

const isCameraOnUser = computed(() => {
  if (!stream.value) return false;

  return stream.value.getVideoTracks().some((track) => {
    const label = track.label.toLowerCase();

    const isScreen =
      label.includes('screen') ||
      label.includes('display') ||
      label.includes('window') ||
      label.includes('tab');

    return track.readyState === 'live' && !isScreen;
  });
});

const startCamera = async (type: CameraType): Promise<boolean> => {
  const getMedia: string =
    { user: 'getUserMedia', display: 'getDisplayMedia' }[type] ||
    'getUserMedia';

  try {
    stopCamera();
    const newStream = await navigator.mediaDevices[getMedia]({
      video: true,
    });
    stream.value = newStream;

    newStream.getTracks().forEach((track) => {
      track.addEventListener('ended', stopCamera);
      track.addEventListener('inactive', stopCamera);
    });

    if (videoElement.value) {
      videoElement.value.srcObject = stream.value;
      await videoElement.value.play();
    }
    return true;
  } catch (err) {
    console.error('Failed to start camera:', err);
    return false;
  }
};

const stopCamera = () => {
  if (stream.value) {
    stream.value.getTracks().forEach((track) => track.stop());
    stream.value = null;
  }

  if (videoElement.value) {
    videoElement.value.srcObject = null;
  }
};

onUnmounted(() => stopCamera());

export const useCamera = () => ({
  videoElement,
  stream,
  isCameraOn,
  isCameraOnUser,
  startCamera,
  stopCamera,
});
